import React, { useEffect, useRef } from 'react';
import { Typography, Button, Box, Container } from '@mui/material';
import { Slide, Fade, Zoom } from '@mui/material';
import { trackScreenView, fireGAEvent } from '../lib/gtag.js';

const BBFTheMoment = () => {
  const sectionRef = useRef();
  const hasTrackedRef = useRef(false);

  const handleDownloadClick = () => {
    window.__skipNextTracking = true;
    setTimeout(() => { window.__skipNextTracking = false; }, 1000);

    fireGAEvent("download_click", {
      event_category: "CTA",
      event_label: "Download Butler Brief",
      screen_name: "BBFTheMoment::Button::DOWNLOAD THE BUTLER BRIEF",
      value: 1,
      version: "1.0.0",
    });

    const target = document.getElementById("download");
    if (target) target.scrollIntoView({ behavior: "smooth" });
    // else console.warn("Download section not found: #download");
  };

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !hasTrackedRef.current && !window.__skipNextTracking) {
          const ua = navigator.userAgent || navigator.vendor || window.opera;
          let platform = 'desktop';
          if (/android/i.test(ua)) platform = 'android';
          else if (/iPad|iPhone|iPod/.test(ua) && !window.MSStream) platform = 'ios';

          trackScreenView(`BBFTheMoment::${platform}`);
          hasTrackedRef.current = true;
          observer.unobserve(entry.target);
        }
      },
      { threshold: 0.5 }
    );

    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <Box
      ref={sectionRef}
      sx={{
        bgcolor: '#000',
        color: '#fff',
        py: { xs: 8, md: 12 },
      }}
    >
      <Container maxWidth="md">
        <Typography variant="h6" sx={{ opacity: 0.8, fontSize: { xs: '0.9rem', md: '1rem' }, mb: 2 }}>
          Butler, PA – 7/13/2024
        </Typography>

        <Button
          variant="outlined"
          color="inherit"
          onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
          sx={{ mb: 4 }}
        >
          Watch the Butler Bullseye Trap – Video ABOVE
        </Button>

        <Fade in timeout={800}>
          <Typography variant="h3" sx={{ fontWeight: 'bold', mb: 3, fontSize: { xs: '1.5rem', md: '2.5rem' } }}>
            Who Put Thomas Crooks on That Roof on 7/13/2024?
          </Typography>
        </Fade>

        <Fade in timeout={1200}>
          <Typography paragraph>
            Joe Biden didn’t just threaten Trump — he built the bullseye in Butler.
          </Typography>
        </Fade>

        <Fade in timeout={1400}>
          <Typography paragraph>
            Biden’s chilling words — <i>"It’s time to put Trump in a bullseye"</i> — sounded like a mob boss giving the kill order.
          </Typography>
        </Fade>

        <Fade in timeout={1600}>
          <Typography paragraph>
            Just five days later, Donald Trump was in that very bullseye, the Butler Bullseye, which Joe Biden and Co. had formally established in 2009, months into his first term as VP. The <b>Kill Zone -- Butler, Pennsylvania</b> — where George Washington himself was nearly assassinated.
          </Typography>
        </Fade>

        <Fade in timeout={1800}>
          <Typography paragraph>
            At the very hour Trump was speaking in Butler, Jill Biden was nearby — concluding a speech at Pittsburgh’s River Casino, 20 miles from the Butler County Fairground. And, no surprise, the River Casino lies exactly on the Rosemont Seneca Corridor, which you will be seeing mapped out in the Butler Bullseye and, as submitted to law enforcement, in the Butler Brief. This was a grotesque political diversion — which doubled down on Joe Biden's death threat, five days before, and which knowingly pulled key Secret Service resources from the Trump Campaign, and the Butler Rally, ensuring Thomas Crooks access to the roof.
          </Typography>
        </Fade>

        <Fade in timeout={2000}>
          <Typography variant="h5" sx={{ mt: 5, fontWeight: 'bold' }}>
            🧨 GAO Report Confirms the Setup:
          </Typography>
        </Fade>

        <Typography paragraph sx={{ color: '#aaa' }}>
          The Government Accountability Office (GAO) is Congress’s official watchdog. Its report revealed:
        </Typography>

        <Fade in timeout={2200}>
          <Box sx={{ pl: 3, mb: 4 }}>
            <ul style={{ fontSize: '1rem', paddingLeft: '20px', marginTop: 0 }}>
              <li>Secret Service had intelligence of the threat <b>10 days before</b> the rally.</li>
              <li>They <b>never notified local or federal law enforcement.</b></li>
              <li>The agent assigned to site security was <b>brand new.</b></li>
              <li>The rally was her <b>first-ever large outdoor event.</b></li>
              <li>The Secret Service drone was <b>down for repairs</b> — but <b>Crooks flew his own.</b></li>
              <li>Agents had already shared <b>photos of Crooks</b> acting suspiciously.</li>
              <li><b>Six agents were suspended</b> — <i>yet no one was fired.</i></li>
              <li>They <b>never secured the perimeter</b> — their most basic duty.</li>
            </ul>
          </Box>
        </Fade>

        <Fade in timeout={2400}>
          <Typography paragraph sx={{ fontStyle: 'italic' }}>
            Congresswoman Anna Paulina Luna: <b>"It reeks of an inside job."</b>
          </Typography>
        </Fade>

        <Fade in timeout={2600}>
          <Typography paragraph>
            The Butler Brief uncovers how the bullseye was built, why Butler was chosen, and how Biden’s regime used <b>weaponized murder</b> to cling to power.
          </Typography>
        </Fade>

        <Fade in timeout={2800}>
          <Typography paragraph>
            🎯 <b>Butler Bullseye Trap</b> — Robert Antonellis, creator of The Butler Brief, used AI-assisted forensic cartography to prove Biden’s “Butler Bullseye” wasn’t just a metaphor for murder — it marked the real-world kill zone, as Biden and Co. made unmistakably clear.
          </Typography>
        </Fade>

        <Fade in timeout={3000}>
          <Typography paragraph>
            🧷 <b>Biden’s Plot to Win at Any Cost</b> — Download the Butler Brief to read the full criminal complaint.
          </Typography>
        </Fade>

        <Zoom in timeout={1200}>
          <Box sx={{ mt: 5 }}>
            <Button
              variant="contained"
              size="large"
              onClick={handleDownloadClick}
              sx={{
                px: 4,
                py: 2,
                fontSize: "1.2rem",
                fontWeight: "bold",
                borderRadius: 2,
                boxShadow: 3,
                textTransform: "uppercase",
                bgcolor: '#f44336',
                ':hover': { bgcolor: '#d32f2f' },
              }}
            >
              UNCOVER THE PLOT — DOWNLOAD THE BUTLER BRIEF NOW
            </Button>
          </Box>
        </Zoom>
      </Container>
    </Box>
  );
};

export default BBFTheMoment;